import React, { useState, useEffect } from "react";
import "./Asah.css";
import { toast } from "react-toastify";
import { fetchUserData, saveUserData, getCurrentUser } from "../services/api";
import { migrateLegacyKey, userGetItem, userRemoveItem, userSetItem } from "../utils/userStorage";
import GuruRubrikPanel from "../components/GuruRubrikPanel";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";

// PDF Viewer
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

// PDF file lokal
import refleksiPdf from "../assets/Refleksi.pdf";
import diskusiPdf from "../assets/kurikulum.pdf";
import informasiPdf from "../assets/kurikulum.pdf";

// === Definisi rubrik baru (6 aspek × 2 indikator) ===
// Konvensi skor: 4 = Sangat sesuai (paling baik), 1 = Tidak sesuai (paling buruk).
// Total skor langsung dijumlahkan tanpa inversi.
// Total maksimal = 12 indikator × 4 = 48. Nilai akhir = (totalSkor / 48) × 100.
const RUBRIK_ASPEK = [
  {
    no: 1,
    nama: "Cerita",
    indikator: [
      { key: "cerita_1", label: "Judul mencerminkan isi cerita" },
      { key: "cerita_2", label: "Menghadirkan pesan yang jelas" },
    ],
  },
  {
    no: 2,
    nama: "Efektivitas",
    indikator: [
      { key: "efektivitas_1", label: "Bahasa jelas, berdaya komunikatif" },
      { key: "efektivitas_2", label: "Berdaya pikat emosional" },
    ],
  },
  {
    no: 3,
    nama: "Relevansi",
    indikator: [
      { key: "relevansi_1", label: "Kontekstual realitas kehidupan" },
      { key: "relevansi_2", label: "Kontekstual budaya / kearifan lokal" },
    ],
  },
  {
    no: 4,
    nama: "Desain",
    indikator: [
      { key: "desain_1", label: "Kualitas gambar dan suara baik" },
      { key: "desain_2", label: "Pilihan gambar dan suara harmonis" },
    ],
  },
  {
    no: 5,
    nama: "Akurasi",
    indikator: [
      { key: "akurasi_1", label: "Konten edukatif dan reflektif" },
      { key: "akurasi_2", label: "Kebenaran informasi valid" },
    ],
  },
  {
    no: 6,
    nama: "Struktur",
    indikator: [
      { key: "struktur_1", label: "Kelengkapan unsur cerita" },
      { key: "struktur_2", label: "Kelengkapan tahapan alur" },
    ],
  },
];

const RUBRIK_KETERANGAN = [
  { skor: 1, label: "Tidak sesuai" },
  { skor: 2, label: "Kurang sesuai" },
  { skor: 3, label: "Sesuai" },
  { skor: 4, label: "Sangat sesuai" },
];

const SKOR_MAX_TOTAL = RUBRIK_ASPEK.reduce(
  (sum, a) => sum + a.indikator.length, 0
) * 4; // 12 × 4 = 48

function buildEmptyIndikatorState() {
  const obj = {};
  for (const aspek of RUBRIK_ASPEK) {
    for (const ind of aspek.indikator) obj[ind.key] = "";
  }
  return obj;
}

function buildEmptyRubrikState() {
  return {
    namaSiswa: "",
    kelas: "",
    judulVideo: "",
    judulVideoId: "",
    tanggalPenilaian: new Date().toISOString().split("T")[0],
    catatanGuru: "",
    ...buildEmptyIndikatorState(),
  };
}

export default function BelajarPage() {
    const viewerIsGuru = getCurrentUser()?.role === 'guru';
    const [activeTab, setActiveTab] = useState(viewerIsGuru ? "rubrik" : "refleksi");
    const [confirmAction, setConfirmAction] = useState(null);
    
    // State untuk form refleksi
    const [refleksiData, setRefleksiData] = useState({
        nama: "",
        kelas: "",
        semester: "",
        tanggalRefleksi: new Date().toISOString().split('T')[0],
        fact: "Hari ini saya belajar materi tentang menulis narasi audio visual, saya antusias saat memperhatikan guru menyampaikan materi. Tetapi masih ada beberapa kesulitan, khususnya dalam hal menuangkan ide dalam tulisan, dan menentukan visualisasi maupun audio yang sesuai dengan naskah cerita. Meskipun demikian, secara keseluruhan saya tertarik mempelajari materi ini lebih dalam lagi.",
        feeling: "Saya senang dan antusias dengan materi ini, saya tertarik mendalami multimedia yang disampaikan guru, saya semangat membuat video animasi dan senang mendapat tantangan belajar yang luar biasa.",
        finding: "Saya menyadari bahwa materi ini sangat menantang dan membuat saya harus berpikir lebih keras. Saya juga menyadari bahwa menggunakan media interaktif seperti ini butuh pemahaman ekstra dan mengasah keterampilan digital yang menantang juga. Namun hasil karya yang saya buat sangat mengagumkan, membuat pembelajaran lebih mudah dipahami karena bisa langsung praktik, menyenangkan, dan memberikan pengalaman baru yang tak terlupakan.",
        future: "Kedepannya, saya akan mencoba lagi membuat yang lebih baik dan sistematis, sehingga karya yang saya hasilkan jauh lebih baik. Saya juga akan membuat beberapa karya sehingga memiliki bank video cerita budaya lokal sendiri untuk dipamerkan kepada khalayak. Saya akan lebih banyak berlatih dan memahami cerita serta budaya agar kemampuan saya semakin meningkat."
    });

    // State untuk rubrik penilaian (struktur dinamis dari RUBRIK_ASPEK)
    const [rubrikData, setRubrikData] = useState(buildEmptyRubrikState);

    // [DINONAKTIFKAN SEMENTARA] Daftar storyboard untuk dropdown "Judul Video".
    // Saat ini judul video diketik manual oleh user. Aktifkan kembali blok di
    // bawah jika ingin memilih dari daftar storyboard yang sudah tersimpan.
    // const [storyboardList, setStoryboardList] = useState([]);
    // const [loadingStoryboards, setLoadingStoryboards] = useState(false);

    // Hasil agregasi (total skor & nilai akhir)
    const [totalSkor, setTotalSkor] = useState(0);
    const [nilaiAkhir, setNilaiAkhir] = useState(0);
    const [keterangan, setKeterangan] = useState("");

    const handleTab = (tab) => setActiveTab(tab);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setRefleksiData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRubrikInputChange = (e) => {
        const { name, value } = e.target;
        setRubrikData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // [DINONAKTIFKAN SEMENTARA] Pemilihan judul video dari daftar storyboard.
    // Saat ini judul video diketik manual lewat input text. Pertahankan blok
    // di bawah ini agar mudah diaktifkan kembali nanti.
    //
    // const handleSelectStoryboard = (e) => {
    //     const id = e.target.value;
    //     if (!id) {
    //         setRubrikData(prev => ({ ...prev, judulVideo: "", judulVideoId: "" }));
    //         return;
    //     }
    //     const sb = storyboardList.find(s => s._id === id);
    //     setRubrikData(prev => ({
    //         ...prev,
    //         judulVideoId: id,
    //         judulVideo: sb?.title || "",
    //     }));
    // };
    //
    // const fetchStoryboardList = useCallback(async () => {
    //     setLoadingStoryboards(true);
    //     try {
    //             headers: authHeaders(),
    //         });
    //         if (res.status === 401) {
    //             console.warn('Sesi habis / belum login — daftar storyboard tidak bisa diambil.');
    //             setStoryboardList([]);
    //             return;
    //         }
    //         const json = await res.json();
    //         const items = (json?.data || []).map(s => ({
    //             _id: s._id,
    //             title: s.title || 'Tanpa Judul',
    //             ownerName: s?.owner?.nama || '',
    //             ownerKelas: s?.owner?.kelas || '',
    //         }));
    //         setStoryboardList(items);
    //     } catch (err) {
    //         console.error('Gagal ambil daftar storyboard:', err);
    //         setStoryboardList([]);
    //     } finally {
    //         setLoadingStoryboards(false);
    //     }
    // }, []);
    //
    // useEffect(() => {
    //     if (activeTab === 'rubrik' && storyboardList.length === 0 && !loadingStoryboards) {
    //         fetchStoryboardList();
    //     }
    // }, [activeTab, storyboardList.length, loadingStoryboards, fetchStoryboardList]);

    // Hitung skor + nilai akhir setiap kali rubrikData berubah.
    // Konvensi: skor 4 = paling baik. Poin per indikator = skor langsung (tanpa inversi).
    useEffect(() => {
        let skor = 0;
        for (const aspek of RUBRIK_ASPEK) {
            for (const ind of aspek.indikator) {
                const s = parseInt(rubrikData[ind.key] || 0, 10);
                if (s >= 1 && s <= 4) skor += s;
            }
        }
        setTotalSkor(skor);

        const nilai = Math.round((skor / SKOR_MAX_TOTAL) * 100);
        setNilaiAkhir(nilai);

        let ket = "Belum Dinilai";
        if (skor > 0) {
            if (nilai >= 81) ket = "Sangat Baik 🏆";
            else if (nilai >= 61) ket = "Baik 👍";
            else if (nilai >= 41) ket = "Cukup ✅";
            else ket = "Kurang 📝";
        }
        setKeterangan(ket);
    }, [rubrikData]);

    const handlePrintRubrik = () => {
        const printWindow = window.open('', '_blank');
        const escape = (s) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;' }[c]));

        const aspekRows = RUBRIK_ASPEK.map(aspek => {
            const rowspan = aspek.indikator.length;
            return aspek.indikator.map((ind, i) => {
                const skor = rubrikData[ind.key];
                const skorCells = [1,2,3,4].map(n => `<td class="${String(n) === String(skor) ? 'cell-checked' : ''}">${String(n) === String(skor) ? '✓' : ''}</td>`).join('');
                return `
                    <tr>
                        ${i === 0 ? `<td rowspan="${rowspan}" class="col-no">${aspek.no}</td>` : ''}
                        ${i === 0 ? `<td rowspan="${rowspan}" class="col-aspek">${escape(aspek.nama)}</td>` : ''}
                        <td class="col-indikator">${escape(ind.label)}</td>
                        ${skorCells}
                    </tr>
                `;
            }).join('');
        }).join('');

        const ketRows = RUBRIK_KETERANGAN.map(k => `<tr><td class="ket-skor">${k.skor}</td><td>${k.label}</td></tr>`).join('');

        const printHtml = `
        <!DOCTYPE html>
        <html lang="id">
        <head>
            <meta charset="UTF-8">
            <title>Rubrik Penilaian — ${escape(rubrikData.namaSiswa || 'Siswa')}</title>
            <style>
                * { box-sizing: border-box; }
                body { font-family: "Arial", sans-serif; margin: 25px; line-height: 1.5; color: #1f2d3d; }
                h2 { text-align: center; margin: 0 0 4px; letter-spacing: 0.5px; }
                h3 { text-align: center; margin: 0 0 18px; font-weight: 500; color: #555; }
                .meta-table { width: 100%; border-collapse: collapse; margin: 14px 0 22px; font-size: 12px; }
                .meta-table td { padding: 6px 10px; border: 1px solid #d0d7de; vertical-align: top; }
                .meta-label { width: 140px; font-weight: bold; background: #f6f8fa; }
                table.rubrik { width: 100%; border-collapse: collapse; margin: 6px 0 18px; font-size: 11.5px; }
                table.rubrik th, table.rubrik td { border: 1px solid #444; padding: 7px 6px; text-align: center; vertical-align: middle; }
                table.rubrik th { background: #0984e3; color: #fff; font-weight: bold; }
                .col-no { width: 32px; font-weight: bold; }
                .col-aspek { width: 100px; font-weight: bold; background: #f1f6fb; text-align: left; padding-left: 10px; }
                .col-indikator { text-align: left; padding-left: 10px; }
                .col-skor { width: 32px; }
                .cell-checked { background: #e0f2fe; font-weight: bold; color: #0369a1; }
                .ket-table { border-collapse: collapse; font-size: 11.5px; margin-bottom: 18px; }
                .ket-table td { border: 1px solid #d0d7de; padding: 5px 14px; }
                .ket-skor { width: 38px; text-align: center; font-weight: bold; background: #f6f8fa; }
                .ket-row { display: flex; gap: 24px; align-items: flex-start; flex-wrap: wrap; margin-bottom: 18px; }
                .ket-section { flex: 0 0 auto; }
                .konversi-table { border-collapse: collapse; font-size: 11.5px; }
                .konversi-table th, .konversi-table td { border: 1px solid #d0d7de; padding: 5px 14px; text-align: center; }
                .konversi-table th { background: #f6f8fa; }
                .summary-box { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 14px 0; }
                .summary-card { border: 1px solid #d0d7de; border-radius: 8px; padding: 12px; text-align: center; background: #f7fafc; }
                .summary-label { font-size: 11px; color: #57606a; text-transform: uppercase; letter-spacing: 0.4px; }
                .summary-value { font-size: 20px; font-weight: bold; color: #0369a1; margin-top: 4px; }
                .catatan-section { margin: 18px 0; padding: 12px 14px; border: 1px solid #d0d7de; border-radius: 6px; background: #fffefa; }
                .catatan-section h4 { margin: 0 0 6px; }
                .signature { margin-top: 40px; display: flex; justify-content: flex-end; }
                .signature-box { text-align: center; min-width: 220px; }
                .signature-line { margin-top: 60px; border-top: 1px solid #444; padding-top: 4px; }
                @media print { body { margin: 12mm; } @page { margin: 12mm; } .no-print { display: none; } }
            </style>
        </head>
        <body>
            <h2>RUBRIK PENILAIAN</h2>
            <h3>Digital Storytelling — Pengembangan Multimedia Interaktif</h3>

            <table class="meta-table">
                <tr>
                    <td class="meta-label">Nama Siswa</td>
                    <td>${escape(rubrikData.namaSiswa) || '..............................'}</td>
                    <td class="meta-label">Kelas</td>
                    <td>${escape(rubrikData.kelas) || '..............................'}</td>
                </tr>
                <tr>
                    <td class="meta-label">Judul Video</td>
                    <td>${escape(rubrikData.judulVideo) || '..............................'}</td>
                    <td class="meta-label">Tanggal Penilaian</td>
                    <td>${escape(rubrikData.tanggalPenilaian) || '..............................'}</td>
                </tr>
            </table>

            <table class="rubrik">
                <thead>
                    <tr>
                        <th rowspan="2">No</th>
                        <th rowspan="2">Aspek Penilaian</th>
                        <th rowspan="2">Indikator</th>
                        <th colspan="4">Skor (1–4)</th>
                    </tr>
                    <tr>
                        <th class="col-skor">1</th>
                        <th class="col-skor">2</th>
                        <th class="col-skor">3</th>
                        <th class="col-skor">4</th>
                    </tr>
                </thead>
                <tbody>
                    ${aspekRows}
                </tbody>
            </table>

            <div class="ket-row">
                <div class="ket-section">
                    <strong>Keterangan Skor:</strong>
                    <table class="ket-table" style="margin-top:6px;">${ketRows}</table>
                </div>
                <div class="ket-section">
                    <strong>Konversi Nilai Akhir:</strong>
                    <table class="konversi-table" style="margin-top:6px;">
                        <tr><th>Rentang</th><th>Kategori</th></tr>
                        <tr><td>81 – 100</td><td>Sangat Baik</td></tr>
                        <tr><td>61 – 80</td><td>Baik</td></tr>
                        <tr><td>41 – 60</td><td>Cukup</td></tr>
                        <tr><td>≤ 40</td><td>Kurang</td></tr>
                    </table>
                </div>
            </div>

            <div class="summary-box">
                <div class="summary-card">
                    <div class="summary-label">Total Skor</div>
                    <div class="summary-value">${totalSkor} / ${SKOR_MAX_TOTAL}</div>
                </div>
                <div class="summary-card">
                    <div class="summary-label">Nilai Akhir</div>
                    <div class="summary-value">${nilaiAkhir}</div>
                </div>
                <div class="summary-card">
                    <div class="summary-label">Kategori</div>
                    <div class="summary-value">${escape(keterangan)}</div>
                </div>
            </div>

            <div class="catatan-section">
                <h4>Catatan Guru</h4>
                <p>${escape(rubrikData.catatanGuru) || 'Tidak ada catatan.'}</p>
            </div>

            <div class="signature">
                <div class="signature-box">
                    <div>Penilai,</div>
                    <div class="signature-line">(.............................................)</div>
                </div>
            </div>
        </body>
        </html>
        `;

        printWindow.document.write(printHtml);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    const handleSaveRubrik = async () => {
        userSetItem('rubrikData', JSON.stringify(rubrikData));
        try {
            await saveUserData('rubrikData', rubrikData);
            toast.success('Rubrik penilaian berhasil disimpan!');
        } catch (error) {
            console.error('Gagal sync rubrik ke server:', error);
            toast.warn('Rubrik disimpan lokal, tetapi gagal disinkronkan ke server.');
        }
    };

    const handleResetRubrik = () => {
        setConfirmAction('reset-rubrik');
    };

    const confirmResetRubrik = () => {
        setRubrikData(buildEmptyRubrikState());
        toast.info('Rubrik direset ke nilai awal');
        setConfirmAction(null);
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        
        const printHtml = `
        <!DOCTYPE html>
        <html lang="id">
        <head>
        <meta charset="UTF-8">
        <title>Refleksi Pembelajaran</title>
        <style>
            body {
                font-family: "Times New Roman", serif;
                margin: 40px;
                line-height: 1.6;
            }
            h1 {
                text-align: center;
                text-transform: uppercase;
                margin-bottom: 30px;
            }
            .field {
                margin-bottom: 10px;
            }
            .label {
                font-weight: bold;
                width: 160px;
                display: inline-block;
            }
            h2 {
                margin-top: 30px;
                border-bottom: 1px solid #000;
                padding-bottom: 4px;
            }
            p {
                text-align: justify;
                margin: 15px 0;
                line-height: 1.8;
            }
            .signature {
                margin-top: 50px;
                text-align: right;
            }
            @media print {
                body {
                    margin: 20mm;
                }
                @page {
                    margin: 20mm;
                }
            }
        </style>
        </head>
        <body>
        <h1>Refleksi Pembelajaran</h1>
        <div class="field"><span class="label">Nama</span>: ${refleksiData.nama || "........................................"}</div>
        <div class="field"><span class="label">Kelas</span>: ${refleksiData.kelas || "........................................"}</div>
        <div class="field"><span class="label">Semester</span>: ${refleksiData.semester || "........................................"}</div>
        <div class="field"><span class="label">Tanggal Refleksi</span>: ${refleksiData.tanggalRefleksi || "........................................"}</div>
        
        <h2>Fact (Fakta Peristiwa)</h2>
        <p>${refleksiData.fact}</p>
        
        <h2>Feeling (Perasaan)</h2>
        <p>${refleksiData.feeling}</p>
        
        <h2>Finding (Temuan Baru Hasil Belajar)</h2>
        <p>${refleksiData.finding}</p>
        
        <h2>Future (Rencana Selanjutnya)</h2>
        <p>${refleksiData.future}</p>
        
        <div class="signature">
            <p>Hormat saya,</p>
            <br><br><br>
            <p>${refleksiData.nama || "........................................"}</p>
        </div>
        </body>
        </html>
        `;
        
        printWindow.document.write(printHtml);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    const handleSave = async () => {
        userSetItem('refleksiData', JSON.stringify(refleksiData));
        try {
            await saveUserData('refleksiData', refleksiData);
            toast.success('Refleksi berhasil disimpan!');
        } catch (error) {
            console.error('Gagal sync refleksi ke server:', error);
            toast.warn('Refleksi disimpan lokal, tetapi gagal disinkronkan ke server.');
        }
    };

    const handleReset = () => {
        setConfirmAction('reset-refleksi');
    };

    const confirmResetRefleksi = () => {
        setRefleksiData({
            nama: "",
            kelas: "",
            semester: "",
            tanggalRefleksi: new Date().toISOString().split('T')[0],
            fact: "Hari ini saya belajar materi tentang menulis narasi audio visual, saya antusias saat memperhatikan guru menyampaikan materi. Tetapi masih ada beberapa kesulitan, khususnya dalam hal menuangkan ide dalam tulisan, dan menentukan visualisasi maupun audio yang sesuai dengan naskah cerita. Meskipun demikian, secara keseluruhan saya tertarik mempelajari materi ini lebih dalam lagi.",
            feeling: "Saya senang dan antusias dengan materi ini, saya tertarik mendalami multimedia yang disampaikan guru, saya semangat membuat video animasi dan senang mendapat tantangan belajar yang luar biasa.",
            finding: "Saya menyadari bahwa materi ini sangat menantang dan membuat saya harus berpikir lebih keras. Saya juga menyadari bahwa menggunakan media interaktif seperti ini butuh pemahaman ekstra dan mengasah keterampilan digital yang menantang juga. Namun hasil karya yang saya buat sangat mengagumkan, membuat pembelajaran lebih mudah dipahami karena bisa langsung praktik, menyenangkan, dan memberikan pengalaman baru yang tak terlupakan.",
            future: "Kedepannya, saya akan mencoba lagi membuat yang lebih baik dan sistematis, sehingga karya yang saya hasilkan jauh lebih baik. Saya juga akan membuat beberapa karya sehingga memiliki bank video cerita budaya lokal sendiri untuk dipamerkan kepada khalayak. Saya akan lebih banyak berlatih dan memahami cerita serta budaya agar kemampuan saya semakin meningkat."
        });
        toast.info('Refleksi dikembalikan ke nilai default');
        setConfirmAction(null);
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: 'Refleksi Pembelajaran',
                text: `Refleksi pembelajaran oleh ${refleksiData.nama || 'Siswa'}`,
                url: window.location.href,
            })
            .catch(error => console.log('Error sharing:', error));
        } else {
            toast.info('Fitur berbagi tidak didukung di browser ini.');
        }
    };

    // Load saved data on component mount (lokal + sync backend)
    useEffect(() => {
        migrateLegacyKey('refleksiData');
        migrateLegacyKey('rubrikData');

        const savedData = userGetItem('refleksiData');
        if (savedData) {
            try {
                setRefleksiData(JSON.parse(savedData));
            } catch (error) {
                console.error('Error loading saved data:', error);
            }
        }

        const savedRubrik = userGetItem('rubrikData');
        if (savedRubrik) {
            try {
                const parsed = JSON.parse(savedRubrik);
                const sampleKey = RUBRIK_ASPEK[0].indikator[0].key;
                if (parsed && Object.prototype.hasOwnProperty.call(parsed, sampleKey)) {
                    setRubrikData({ ...buildEmptyRubrikState(), ...parsed });
                } else {
                    console.info('Schema rubrik lama terdeteksi — direset ke schema baru.');
                    userRemoveItem('rubrikData');
                }
            } catch (error) {
                console.error('Error loading rubrik data:', error);
            }
        }

        (async () => {
            try {
                const [refleksiRes, rubrikRes] = await Promise.all([
                    fetchUserData('refleksiData'),
                    fetchUserData('rubrikData'),
                ]);

                if (refleksiRes?.data) {
                    setRefleksiData((prev) => ({ ...prev, ...refleksiRes.data }));
                    userSetItem('refleksiData', JSON.stringify(refleksiRes.data));
                }

                if (rubrikRes?.data) {
                    const sampleKey = RUBRIK_ASPEK[0].indikator[0].key;
                    if (Object.prototype.hasOwnProperty.call(rubrikRes.data, sampleKey)) {
                        setRubrikData({ ...buildEmptyRubrikState(), ...rubrikRes.data });
                        userSetItem('rubrikData', JSON.stringify(rubrikRes.data));
                    }
                }
            } catch (error) {
                console.warn('Data Asah dari server belum tersedia:', error.message);
            }
        })();
    }, []);

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        defaultScale: "PageFit"
    });

    // Mapping tab ke konten
    const pdfMap = {
        refleksi: refleksiPdf,
        diskusi: diskusiPdf,
        informasi: informasiPdf
    };

    // Data untuk setiap tab
    const tabData = {
        refleksi: {
            title: "🪞 Ruang Refleksi",
            description: "Tempat untuk merenung dan mengevaluasi pembelajaranmu",
            color: "#FF6B6B",
            gradient: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
            tips: [
                "Apa yang sudah kamu pelajari hari ini?",
                "Bagian mana yang paling menyenangkan?",
                "Apa yang ingin kamu pelajari lagi?"
            ]
        },
        diskusi: {
            title: "💬 Ruang Diskusi",
            description: "Berbagi ide dan bertukar pikiran dengan teman-teman",
            color: "#4ECDC4",
            gradient: "linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)",
            tips: [
                "Ajukan pertanyaan yang menarik",
                "Dengarkan pendapat temanmu",
                "Berikan masukan yang membangun"
            ]
        },
        rubrik: {
            title: "📊 Rubrik Penilaian",
            description: "Penilaian digital storytelling dengan kriteria terukur",
            color: "#FFD93D",
            gradient: "linear-gradient(135deg, #FFD93D 0%, #FF9A3D 100%)",
            tips: [
                "Beri skor sesuai dengan kriteria",
                "Perhatikan setiap indikator dengan teliti",
                "Berikan catatan yang membangun"
            ]
        },
        informasi: {
            title: "ℹ️ Informasi Belajar",
            description: "Panduan dan informasi penting untuk proses belajarmu",
            color: "#6A0572",
            gradient: "linear-gradient(135deg, #6A0572 0%, #AB83A1 100%)",
            tips: [
                "Simak baik-baik petunjuknya",
                "Catat hal-hal penting",
                "Jangan ragu bertanya jika bingung"
            ]
        }
    };

    const currentTab = tabData[activeTab];

    // Radio Button 1-4 (1 = Sangat sesuai, 4 = Tidak sesuai)
    const RadioGroup = ({ name, value, onChange }) => {
        return (
            <div className="radio-group">
                {[1, 2, 3, 4].map((num) => (
                    <label key={num} className="radio-label">
                        <input
                            type="radio"
                            name={name}
                            value={num}
                            checked={String(value) === String(num)}
                            onChange={onChange}
                        />
                        <span className="radio-text">{num}</span>
                    </label>
                ))}
            </div>
        );
    };

    return (
        <div className="belajar-container">
            {/* Header Section */}
            <div className="belajar-header">
                <div className="header-content">
                    <h1>Dunia Belajar Seru! </h1>
                    <p>Temukan berbagai materi belajar yang menarik dan menyenangkan!</p>
                    <span className={`role-badge ${viewerIsGuru ? 'guru-badge' : 'siswa-badge'}`}>
                        {viewerIsGuru ? '👩‍🏫 Mode Guru — rubrik & penilaian' : '🎓 Mode Siswa — refleksi & diskusi'}
                    </span>
                </div>
            </div>

            {/* Progress Section */}
            <div className="progress-section">
                <div className="progress-info">
                    <span>Progress Belajarmu</span>
                    <span>75%</span>
                </div>
                <div className="progress-bar">
                    <div 
                        className="progress-fill" 
                        style={{width: '75%', background: currentTab.color}}
                    ></div>
                </div>
                <div className="progress-badges">
                    <div className={`badge ${activeTab === 'refleksi' ? 'current' : 'completed'}`}>🪞</div>
                    <div className={`badge ${activeTab === 'diskusi' ? 'current' : activeTab === 'refleksi' ? 'upcoming' : 'completed'}`}>💬</div>
                    <div className={`badge ${activeTab === 'rubrik' ? 'current' : ['refleksi', 'diskusi'].includes(activeTab) ? 'upcoming' : 'completed'}`}>📊</div>
                    <div className={`badge ${activeTab === 'informasi' ? 'current' : 'upcoming'}`}>ℹ️</div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="tab-container">
                <div className="tab-buttons">
                    {Object.entries(tabData).map(([key, tab]) => (
                        <button
                            key={key}
                            className={`tab-button ${activeTab === key ? "active" : ""}`}
                            onClick={() => handleTab(key)}
                            aria-selected={activeTab === key}
                            role="tab"
                            style={{
                                '--tab-color': tab.color,
                                '--tab-gradient': tab.gradient
                            }}
                        >
                            <div className="tab-icon">{tab.title.split(' ')[0]}</div>
                            <div className="tab-content">
                                <div className="tab-title">{tab.title.split(' ').slice(1).join(' ')}</div>
                                <div className="tab-subtitle">{tab.description}</div>
                            </div>
                            <div className="tab-arrow">➡️</div>
                        </button>
                    ))}
                </div>

                {/* Tab Indicator */}
                <div className="tab-indicator">
                    <div 
                        className="indicator" 
                        style={{
                            transform: `translateX(${Object.keys(tabData).indexOf(activeTab) * 100}%)`,
                            background: currentTab.gradient
                        }}
                    ></div>
                </div>
            </div>

            {/* Content Area */}
            <div className="content-area">
                <div className="content-header">
                    <h2>{currentTab.title}</h2>
                    <p>{currentTab.description}</p>
                </div>

                <div className="content-grid">
                    {/* Main Content Section */}
                    <div className="pdf-section">
                        {activeTab === 'refleksi' ? (
                            <div className="refleksi-form-container">
                                <div className="refleksi-header">
                                    <div className="pdf-info">
                                        <h3>📝 Refleksi Pembelajaran</h3>
                                        <span className="pdf-status">✏️ Form Isian Mudah</span>
                                    </div>
                                    <div className="pdf-actions">
                                        <button className="action-btn save" onClick={handleSave}>💾 Simpan</button>
                                        <button className="action-btn print" onClick={handlePrint}>🖨️ Cetak</button>
                                        <button className="action-btn share" onClick={handleShare}>📤 Bagikan</button>
                                        <button className="action-btn reset" onClick={handleReset}>🔄 Reset</button>
                                    </div>
                                </div>
                                
                                <form className="refleksi-form">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="nama">Nama Lengkap</label>
                                            <input
                                                type="text"
                                                id="nama"
                                                name="nama"
                                                value={refleksiData.nama}
                                                onChange={handleInputChange}
                                                placeholder="Masukkan nama lengkap"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="kelas">Kelas</label>
                                            <input
                                                type="text"
                                                id="kelas"
                                                name="kelas"
                                                value={refleksiData.kelas}
                                                onChange={handleInputChange}
                                                placeholder="Contoh: XI IPA 2"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="semester">Semester</label>
                                                <input
                                                type="text"
                                                id="semester"
                                                name="semester"
                                                value={refleksiData.semester}
                                                onChange={handleInputChange}
                                                placeholder="Contoh: Semester 1"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="tanggalRefleksi">Tanggal Refleksi</label>
                                            <input
                                                type="date"
                                                id="tanggalRefleksi"
                                                name="tanggalRefleksi"
                                                value={refleksiData.tanggalRefleksi}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-section">
                                        <h4>Fact (Fakta Peristiwa)</h4>
                                        <p className="section-description">Apa yang terjadi selama pembelajaran?</p>
                                        <textarea
                                            name="fact"
                                            value={refleksiData.fact}
                                            onChange={handleInputChange}
                                            rows="4"
                                            placeholder="Jelaskan fakta-fakta yang terjadi selama pembelajaran..."
                                        />
                                    </div>

                                    <div className="form-section">
                                        <h4>Feeling (Perasaan)</h4>
                                        <p className="section-description">Bagaimana perasaan Anda selama pembelajaran?</p>
                                        <textarea
                                            name="feeling"
                                            value={refleksiData.feeling}
                                            onChange={handleInputChange}
                                            rows="4"
                                            placeholder="Bagaimana perasaan Anda? Senang, antusias, bingung, atau lainnya?"
                                        />
                                    </div>

                                    <div className="form-section">
                                        <h4>Finding (Temuan Baru Hasil Belajar)</h4>
                                        <p className="section-description">Apa yang Anda temukan atau pelajari?</p>
                                        <textarea
                                            name="finding"
                                            value={refleksiData.finding}
                                            onChange={handleInputChange}
                                            rows="4"
                                            placeholder="Apa hal baru yang Anda pelajari atau temukan?"
                                        />
                                    </div>

                                    <div className="form-section">
                                        <h4>Future (Rencana Selanjutnya)</h4>
                                        <p className="section-description">Apa rencana Anda setelah pembelajaran ini?</p>
                                        <textarea
                                            name="future"
                                            value={refleksiData.future}
                                            onChange={handleInputChange}
                                            rows="4"
                                            placeholder="Apa yang akan Anda lakukan selanjutnya untuk meningkatkan pemahaman?"
                                        />
                                    </div>
                                </form>
                            </div>
                        ) : activeTab === 'rubrik' ? (
                            viewerIsGuru ? (
                                <GuruRubrikPanel />
                            ) : (
                            <div className="rubrik-form-container">
                                <div className="rubrik-header">
                                    <div className="pdf-info">
                                        <h3>📊 Rubrik Penilaian Digital Storytelling</h3>
                                        <span className="pdf-status">🎯 Penilaian Formatif</span>
                                    </div>
                                    <div className="pdf-actions">
                                        <button className="action-btn save" onClick={handleSaveRubrik}>💾 Simpan</button>
                                        <button className="action-btn print" onClick={handlePrintRubrik}>🖨️ Cetak</button>
                                        <button className="action-btn reset" onClick={handleResetRubrik}>🔄 Reset</button>
                                    </div>
                                </div>
                                
                                <div className="rubrik-form">
                                    {/* Informasi Siswa */}
                                    <div className="rubrik-student-info">
                                        <div className="form-group">
                                            <label htmlFor="namaSiswa">Nama Siswa</label>
                                            <input
                                                type="text"
                                                id="namaSiswa"
                                                name="namaSiswa"
                                                value={rubrikData.namaSiswa}
                                                onChange={handleRubrikInputChange}
                                                placeholder="Masukkan nama siswa"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="kelas">Kelas</label>
                                            <input
                                                type="text"
                                                id="kelas"
                                                name="kelas"
                                                value={rubrikData.kelas}
                                                onChange={handleRubrikInputChange}
                                                placeholder="Contoh: XI IPA 2"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="judulVideo">Judul Video</label>
                                            <input
                                                type="text"
                                                id="judulVideo"
                                                name="judulVideo"
                                                value={rubrikData.judulVideo}
                                                onChange={handleRubrikInputChange}
                                                placeholder="Ketik judul video yang dinilai"
                                            />
                                            {/*
                                              [DINONAKTIFKAN SEMENTARA]
                                              Versi dropdown (memilih dari daftar storyboard yang tersimpan).
                                              Aktifkan kembali jika nanti ingin auto-pilih dari list.

                                              <select
                                                  id="judulVideoId"
                                                  name="judulVideoId"
                                                  value={rubrikData.judulVideoId}
                                                  onChange={handleSelectStoryboard}
                                                  disabled={loadingStoryboards}
                                              >
                                                  <option value="">
                                                      {loadingStoryboards
                                                          ? "Memuat daftar video..."
                                                          : storyboardList.length === 0
                                                              ? "— Belum ada video tersimpan —"
                                                              : "— Pilih video yang dinilai —"}
                                                  </option>
                                                  {storyboardList.map(sb => {
                                                      const ownerLabel = sb.ownerName
                                                          ? ` — ${sb.ownerName}${sb.ownerKelas ? ` (${sb.ownerKelas})` : ''}`
                                                          : '';
                                                      return (
                                                          <option key={sb._id} value={sb._id}>
                                                              {sb.title}{ownerLabel}
                                                          </option>
                                                      );
                                                  })}
                                              </select>
                                              {rubrikData.judulVideo && !rubrikData.judulVideoId && (
                                                  <small style={{ color: '#666', marginTop: 4, display: 'block' }}>
                                                      Manual: {rubrikData.judulVideo}
                                                  </small>
                                              )}
                                            */}
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="tanggalPenilaian">Tanggal Penilaian</label>
                                            <input
                                                type="date"
                                                id="tanggalPenilaian"
                                                name="tanggalPenilaian"
                                                value={rubrikData.tanggalPenilaian}
                                                onChange={handleRubrikInputChange}
                                            />
                                        </div>
                                    </div>

                                    {/* Tabel Rubrik (dinamis dari RUBRIK_ASPEK) */}
                                    <div className="rubrik-table-container">
                                        <table className="rubrik-table">
                                            <thead>
                                                <tr>
                                                    <th>No</th>
                                                    <th>Aspek Penilaian</th>
                                                    <th>Indikator</th>
                                                    <th>Skor (1–4)</th>
                                                    <th>Keterangan</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {RUBRIK_ASPEK.map((aspek) =>
                                                    aspek.indikator.map((ind, idx) => (
                                                        <tr key={ind.key} className={idx === 0 ? 'aspek-header' : ''}>
                                                            {idx === 0 && <td rowSpan={aspek.indikator.length}>{aspek.no}</td>}
                                                            {idx === 0 && <td rowSpan={aspek.indikator.length}>{aspek.nama}</td>}
                                                            <td>{ind.label}</td>
                                                            <td>
                                                                <RadioGroup
                                                                    name={ind.key}
                                                                    value={rubrikData[ind.key]}
                                                                    onChange={handleRubrikInputChange}
                                                                />
                                                            </td>
                                                            {idx === 0 && (
                                                                <td rowSpan={aspek.indikator.length} className="aspek-description">
                                                                    {RUBRIK_KETERANGAN.map(k => (
                                                                        <div key={k.skor}>{k.skor} = {k.label}</div>
                                                                    ))}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Konversi nilai akhir (referensi cepat untuk guru) */}
                                    <div className="rubrik-konversi">
                                        <strong>Konversi Nilai Akhir:</strong>
                                        <div className="konversi-grid">
                                            <span><b>81–100</b> · Sangat Baik</span>
                                            <span><b>61–80</b> · Baik</span>
                                            <span><b>41–60</b> · Cukup</span>
                                            <span><b>≤ 40</b> · Kurang</span>
                                        </div>
                                    </div>

                                    {/* Catatan dan Hasil */}
                                    <div className="rubrik-results">
                                        <div className="result-card">
                                            <h4>📊 Hasil Penilaian</h4>
                                            <div className="result-grid">
                                                <div className="result-item">
                                                    <span className="result-label">Total Skor</span>
                                                    <span className="result-value">{totalSkor} / {SKOR_MAX_TOTAL}</span>
                                                </div>
                                                <div className="result-item">
                                                    <span className="result-label">Nilai Akhir</span>
                                                    <span className="result-value">{nilaiAkhir}</span>
                                                </div>
                                                <div className="result-item">
                                                    <span className="result-label">Kategori</span>
                                                    <span className="result-value keterangan">{keterangan}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="rubrik-notes">
                                            <label htmlFor="catatanGuru">Catatan Guru:</label>
                                            <textarea
                                                id="catatanGuru"
                                                name="catatanGuru"
                                                value={rubrikData.catatanGuru}
                                                onChange={handleRubrikInputChange}
                                                rows="3"
                                                placeholder="Berikan masukan dan saran yang membangun..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            )
                        ) : (
                            <>
                                <div className="pdf-header">
                                    <div className="pdf-info">
                                        <h3>📋 Materi Pembelajaran</h3>
                                        <span className="pdf-status">🟢 Tersedia</span>
                                    </div>
                                    <div className="pdf-actions">
                                        <button className="action-btn download">📥 Unduh</button>
                                        <button className="action-btn print">🖨️ Print</button>
                                        <button className="action-btn share">📤 Bagikan</button>
                                    </div>
                                </div>
                                
                                <div className="pdf-viewer-container">
                                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                                        <Viewer 
                                            fileUrl={pdfMap[activeTab]} 
                                            plugins={[defaultLayoutPluginInstance]} 
                                        />
                                    </Worker>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Tips Section */}
                    <div className="tips-section">
                        <div className="tips-header">
                            <h3>💡 Tips Belajar</h3>
                            <div className="tips-emoji">🎯</div>
                        </div>
                        <div className="tips-list">
                            {currentTab.tips.map((tip, index) => (
                                <div key={index} className="tip-item">
                                    <div className="tip-number">{index + 1}</div>
                                    <div className="tip-text">{tip}</div>
                                </div>
                            ))}
                        </div>
                        
                        {/* Quick Actions */}
                        <div className="quick-actions">
                            <h4>🚀 Aksi Cepat</h4>
                            <div className="action-buttons">
                                <button className="quick-btn bookmark">🔖 Tandai</button>
                                <button className="quick-btn note">📝 Catat</button>
                                <button className="quick-btn question">❓ Tanya</button>
                            </div>
                        </div>

                        {/* Word Count for Refleksi */}
                        {activeTab === 'refleksi' && (
                            <div className="word-count-section">
                                <h4>📊 Statistik Refleksi</h4>
                                <div className="word-count-grid">
                                    <div className="count-box">
                                        <span className="count-number">{refleksiData.fact.split(' ').length}</span>
                                        <span className="count-label">Kata Fact</span>
                                    </div>
                                    <div className="count-box">
                                        <span className="count-number">{refleksiData.feeling.split(' ').length}</span>
                                        <span className="count-label">Kata Feeling</span>
                                    </div>
                                    <div className="count-box">
                                        <span className="count-number">{refleksiData.finding.split(' ').length}</span>
                                        <span className="count-label">Kata Finding</span>
                                    </div>
                                    <div className="count-box">
                                        <span className="count-number">{refleksiData.future.split(' ').length}</span>
                                        <span className="count-label">Kata Future</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Grading Scale for Rubrik */}
                        {activeTab === 'rubrik' && (
                            <div className="grading-scale">
                                <h4>📈 Skala Penilaian</h4>
                                <div className="scale-grid">
                                    <div className="scale-item excellent">
                                        <span className="scale-range">86-100</span>
                                        <span className="scale-grade">Sangat Baik</span>
                                    </div>
                                    <div className="scale-item good">
                                        <span className="scale-range">71-85</span>
                                        <span className="scale-grade">Baik</span>
                                    </div>
                                    <div className="scale-item average">
                                        <span className="scale-range">56-70</span>
                                        <span className="scale-grade">Cukup</span>
                                    </div>
                                    <div className="scale-item poor">
                                        <span className="scale-range">41-55</span>
                                        <span className="scale-grade">Kurang</span>
                                    </div>
                                    <div className="scale-item fail">
                                        <span className="scale-range">0-40</span>
                                        <span className="scale-grade">Sangat Kurang</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Activity Suggestions */}
                <div className="activity-section">
                    <h3>🎨 Aktivitas Seru</h3>
                    <div className="activity-grid">
                        <div className="activity-card">
                            <div className="activity-emoji">✏️</div>
                            <h4>Tulis Refleksi</h4>
                            <p>Bagikan pemikiranmu tentang materi ini</p>
                        </div>
                        <div className="activity-card">
                            <div className="activity-emoji">🎭</div>
                            <h4>Drama Cerita</h4>
                            <p>Perankan cerita favoritmu dengan teman</p>
                        </div>
                        <div className="activity-card">
                            <div className="activity-emoji">🖼️</div>
                            <h4>Buat Komik</h4>
                            <p>Ubah cerita menjadi komik yang menarik</p>
                        </div>
                        <div className="activity-card">
                            <div className="activity-emoji">🎤</div>
                            <h4>Presentasi</h4>
                            <p>Presentasikan yang sudah kamu pelajari</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="belajar-footer">
                <div className="footer-content">
                    <h3>Selamat Belajar! 🎉</h3>
                    <p>Setiap hari adalah kesempatan baru untuk belajar hal seru!</p>
                    <div className="footer-actions">
                        <button className="footer-btn">📚 Materi Lainnya</button>
                        <button className="footer-btn">🎯 Quiz Seru</button>
                        <button className="footer-btn">🏆 Lihat Pencapaian</button>
                    </div>
                </div>
                <div className="footer-decoration">
                    <div className="bouncing-emoji">🎈</div>
                    <div className="bouncing-emoji">🎨</div>
                    <div className="bouncing-emoji">📖</div>
                </div>
            </div>

            <ConfirmDialog
                open={confirmAction === 'reset-rubrik'}
                title="Reset rubrik?"
                message="Semua nilai rubrik akan dikembalikan ke kosong. Lanjutkan?"
                confirmLabel="Ya, reset"
                onConfirm={confirmResetRubrik}
                onCancel={() => setConfirmAction(null)}
            />
            <ConfirmDialog
                open={confirmAction === 'reset-refleksi'}
                title="Reset refleksi?"
                message="Form refleksi akan dikembalikan ke nilai default. Lanjutkan?"
                confirmLabel="Ya, reset"
                onConfirm={confirmResetRefleksi}
                onCancel={() => setConfirmAction(null)}
            />
        </div>
    );
}